import { addDoc, collection, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../shared/storage/firebase';
import type { PartnerDocumentKind } from '../types';

function safeFilename(value: string): string {
  return value.trim().replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-');
}

export async function uploadPartnerBrandingImage(params: {
  partnerId: string;
  file: File;
  kind: 'logo' | 'hero';
  actorId?: string | null;
}): Promise<string> {
  const { partnerId, file, kind, actorId } = params;
  const storagePath = `partners/${partnerId}/branding/${kind}-${Date.now()}-${safeFilename(file.name)}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, {
    contentType: file.type || undefined,
    customMetadata: { partnerId, kind, uploadedBy: actorId || '' },
  });
  const url = await getDownloadURL(fileRef);
  await updateDoc(doc(db, 'partners', partnerId), {
    [kind === 'logo' ? 'branding.logoUrl' : 'branding.heroImageUrl']: url,
    updatedAt: serverTimestamp(),
    updatedBy: actorId || null,
  });
  return url;
}

export async function uploadPartnerDocument(params: {
  partnerId: string;
  file: File;
  kind: PartnerDocumentKind;
  title: string;
  actorId?: string | null;
  requirementId?: string | null;
}): Promise<string> {
  const { partnerId, file, kind, title, actorId, requirementId } = params;
  const storagePath = `partners/${partnerId}/documents/${Date.now()}-${safeFilename(file.name)}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, {
    contentType: file.type || undefined,
    customMetadata: { partnerId, kind, uploadedBy: actorId || '', ...(requirementId ? { requirementId } : {}) },
  });
  const url = await getDownloadURL(fileRef);
  const documentRef = await addDoc(collection(db, 'partners', partnerId, 'documents'), {
    kind,
    title: title.trim() || file.name,
    ...(requirementId ? { requirementId } : {}),
    file: {
      storagePath,
      url,
      filename: file.name,
      contentType: file.type || null,
      sizeBytes: file.size,
      uploadedAt: serverTimestamp(),
      uploadedBy: actorId || null,
    },
    createdAt: serverTimestamp(),
    createdBy: actorId || null,
  });
  return documentRef.id;
}

export async function uploadLocationCampusPhoto(params: {
  locationId: string;
  file: File;
  actorId?: string | null;
}): Promise<string> {
  const { locationId, file, actorId } = params;
  const storagePath = `locations/${locationId}/campusPhotos/${Date.now()}-${safeFilename(file.name)}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, {
    contentType: file.type || undefined,
    customMetadata: { locationId, uploadedBy: actorId || '' },
  });
  const url = await getDownloadURL(fileRef);
  await updateDoc(doc(db, 'locations', locationId), {
    campusPhotoUrls: [url],
    updatedAt: serverTimestamp(),
    updatedBy: actorId || null,
  });
  return url;
}

export async function removeLocationCampusPhoto(params: {
  locationId: string;
  actorId?: string | null;
}): Promise<void> {
  const { locationId, actorId } = params;
  await updateDoc(doc(db, 'locations', locationId), {
    campusPhotoUrls: [],
    updatedAt: serverTimestamp(),
    updatedBy: actorId || null,
  });
}
